import React from 'react'
import { Typography, TextField, Box, Button } from '@mui/material'
import Alert from '@mui/material/Alert'
import { IStockCardBatch } from 'interfaces/IStockCard'
import { IBatch } from 'interfaces/IPremix'
import { VALUE_IN, VALUE_OUT } from 'enums/stockCard'
import LoadBatchAutocomplete from 'shared/autocomplete/loadBatchAutocomplete'
import { formatStockQuantity } from 'components/inventory/helpers'
import { clampDecimalPlaces } from 'utils/clampDecimalPlaces'

interface Props {
  stockCardBatch: IStockCardBatch
  inOutDirection: string
  matchingBatch?: IBatch
  onChange: (
    value: any,
    key: string,
    incomingStockCardBatch: IStockCardBatch
  ) => void
  disabled?: boolean
  isBatchLoading?: boolean
  isConfirmed?: boolean
  premixId?: string
  isEditing?: boolean
  onRemove?: () => void
  outgoingDestination?: string
  isSuperuser?: boolean
}

const FieldRow = ({ label, children }: { label: string, children: React.ReactNode }) => (
  <Box display="flex" borderBottom="1px solid #eee" py={1.5} alignItems="center">
    <Typography style={{ width: '30%', fontWeight: 500, fontSize: '0.95rem', color: '#666' }}>
      {label}
    </Typography>
    <Box flexGrow={1}>
      {children}
    </Box>
  </Box>
)

export default function StockCardBatchItem(props: Props) {
  const {
    stockCardBatch,
    inOutDirection,
    matchingBatch,
    onChange,
    disabled,
    isBatchLoading,
    isConfirmed,
    premixId,
    isEditing,
    onRemove,
  } = props

  const isEditable = isEditing || !isConfirmed

  const qtyLabel = inOutDirection === VALUE_IN ? 'Qty Mau Dimasukan' : 'Qty Diminta'
  const confirmedQtyLabel = inOutDirection === VALUE_IN ? 'Jumlah Diterima' : 'Jumlah Dikeluarkan'
  const currentStockLabel = 'Stok Sekarang'

  return (
    <Box width="100%" py={1}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
          Item Bets
        </Typography>
        {onRemove && (
          <Button color="secondary" onClick={onRemove} size="small" variant="outlined">
            Hapus
          </Button>
        )}
      </Box>

      {isEditable ? (
        <Box>
          <FieldRow label="No. Bets">
            {isEditing ? (
              <LoadBatchAutocomplete
                name={stockCardBatch.batchNumber}
                premixId={premixId}
                onChange={(e: IBatch) => {
                  onChange(e.batchNumber, 'batchNumber', stockCardBatch)
                }}
              />
            ) : (
              <Typography style={{ fontWeight: 600 }}>{stockCardBatch.batchNumber}</Typography>
            )}
          </FieldRow>

          <FieldRow label={currentStockLabel}>
            {isBatchLoading ? (
              <Typography color="textSecondary" style={{ fontStyle: 'italic' }}>
                Loading...
              </Typography>
            ) : (
              <Typography style={{ fontWeight: 600 }}>
                {formatStockQuantity(matchingBatch?.currentQuantity ?? 0)}
              </Typography>
            )}
          </FieldRow>

          <FieldRow label={qtyLabel}>
            {isEditing ? (
              <TextField
                value={stockCardBatch.quantity}
                onChange={(e) => onChange(clampDecimalPlaces(e.target.value), 'quantity', stockCardBatch)}
                variant="outlined"
                size="small"
                fullWidth
              />
            ) : (
              <Typography style={{ fontWeight: 600 }}>{formatStockQuantity(stockCardBatch.quantity)}</Typography>
            )}
          </FieldRow>

          <FieldRow label={confirmedQtyLabel}>
            <TextField
              required
              autoComplete="off"
              variant="outlined"
              fullWidth
              size="small"
              value={stockCardBatch.confirmedQuantity ?? ''}
              onChange={(e) =>
                onChange(
                  clampDecimalPlaces(e.target.value),
                  'confirmedQuantity',
                  stockCardBatch
                )
              }
              disabled={disabled && stockCardBatch.confirmedQuantity != null}
            />
          </FieldRow>

          <FieldRow label="Kode Ruahan">
            <Typography style={{ fontWeight: 600 }}>{premixId}</Typography>
          </FieldRow>
        </Box>
      ) : (
        <Box>
          <FieldRow label="Kode Ruahan">
            <Typography style={{ fontWeight: 600 }}>{premixId || '-'}</Typography>
          </FieldRow>
          <FieldRow label="No. Bets">
            <Typography style={{ fontWeight: 600 }}>{stockCardBatch.batchNumber || '-'}</Typography>
          </FieldRow>
          <FieldRow label={currentStockLabel}>
            {isBatchLoading ? (
              <Typography color="textSecondary" style={{ fontStyle: 'italic' }}>
                Loading...
              </Typography>
            ) : (
              <Typography style={{ fontWeight: 600 }}>
                {formatStockQuantity(matchingBatch?.currentQuantity ?? 0)}
              </Typography>
            )}
          </FieldRow>
          <FieldRow label={qtyLabel}>
            <Typography style={{ fontWeight: 600 }}>{formatStockQuantity(stockCardBatch.quantity ?? 0)}</Typography>
          </FieldRow>
          <FieldRow label={confirmedQtyLabel}>
            <Typography style={{ fontWeight: 600 }}>{formatStockQuantity(stockCardBatch.confirmedQuantity)}</Typography>
          </FieldRow>
        </Box>
      )}

      {inOutDirection === VALUE_OUT &&
        !disabled &&
        !isBatchLoading &&
        matchingBatch &&
        Number(stockCardBatch.confirmedQuantity ?? 0) >
        Number(matchingBatch.currentQuantity ?? 0) && (
          <Box mt={2}>
            <Alert severity="warning">Jumlah Tidak Boleh Lebih Dari Stok (Stok: {formatStockQuantity(matchingBatch.currentQuantity ?? 0)})</Alert>
          </Box>
        )}
    </Box>
  )
}
